import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import { alpha, Box, Chip, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useMemo, useState } from 'react'
import { useRoadmap } from './RoadmapContext'
import { CatalogItem, classifyItem, getPhaseShortLabel, Phase } from './types'
import { getVariantLabel, ItemGroup } from './variantGrouping'

export const VariantGroupCard: React.FC<{
  group: ItemGroup
  activePhaseItemIds: Set<string>
  getItemPhases: (productId: string) => Set<Phase>
  removeFromAll: (productId: string) => void
  addItem: (item: CatalogItem) => void
  handleOpenMultiMonth: (event: React.MouseEvent<HTMLElement>, item: CatalogItem) => void
  effectiveRate: number
  slColor: string
  SlIcon: any
  groupLabel: string
  isSetupPhase: boolean
  activePhase: Phase
  recommendedSkus: Map<string, string>
}> = ({
  group,
  activePhaseItemIds,
  getItemPhases,
  removeFromAll,
  handleOpenMultiMonth,
  slColor,
  SlIcon,
  groupLabel,
  isSetupPhase,
  activePhase,
  recommendedSkus,
}) => {
  const theme = useTheme()
  const aiAccent = theme.palette.text.ai_accent ?? '#A855F7'
  const { state, addItem, effectiveRate } = useRoadmap()
  // Default to middle variant
  const defaultIndex = Math.floor((group.items.length - 1) / 2)
  const [selectedIndex, setSelectedIndex] = useState(defaultIndex)
  const selectedItem = group.items[selectedIndex]

  // Check if any variant from this group is added in active phase
  const addedVariant = group.items.find(item => activePhaseItemIds.has(item.products_uuid))
  const isAnyAdded = !!addedVariant
  const isSelectedAdded = activePhaseItemIds.has(selectedItem.products_uuid)

  // Check if ANY variant is AI-recommended
  const anyRecommended = group.items.some(i => i.sku && recommendedSkus.has(i.sku))
  const selectedAiReason = selectedItem.sku ? recommendedSkus.get(selectedItem.sku) : undefined

  // All phases any variant appears in
  const allVariantPhases = useMemo(() => {
    const phases = new Set<Phase>()
    group.items.forEach(item => {
      getItemPhases(item.products_uuid).forEach(p => phases.add(p))
    })
    return phases
  }, [group.items, getItemPhases])

  const dollarValue = (selectedItem.points || 0) * effectiveRate
  const cat = classifyItem(selectedItem)

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: isAnyAdded
          ? alpha(theme.palette.text.complete, 0.4)
          : anyRecommended
          ? alpha(aiAccent, 0.4)
          : 'divider',
        bgcolor: isAnyAdded
          ? alpha(theme.palette.text.complete, 0.04)
          : anyRecommended
          ? alpha(aiAccent, 0.04)
          : 'background.paper',
        transition: 'all 0.15s',
        overflow: 'hidden',
        '&:hover': {
          borderColor: isAnyAdded
            ? alpha(theme.palette.text.complete, 0.5)
            : anyRecommended
            ? alpha(aiAccent, 0.6)
            : alpha(theme.palette.text.primary, 0.15),
          transform: 'translateY(-1px)',
          boxShadow: anyRecommended ? `0 2px 12px ${alpha(aiAccent, 0.2)}` : '0 2px 8px rgba(0,0,0,0.15)',
        },
      }}
    >
      {/* Card header */}
      <Box sx={{ p: 1.5, pb: 1 }}>
        {/* Category + service line + AI badges */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Chip
            label={cat === 'strategic' ? 'Strategic' : 'Executable'}
            size="small"
            sx={{
              height: 20,
              fontSize: 10,
              fontWeight: 600,
              bgcolor:
                cat === 'strategic' ? alpha(theme.palette.text.link, 0.12) : alpha(theme.palette.text.highlight, 0.12),
              color: cat === 'strategic' ? theme.palette.text.link : theme.palette.text.highlight,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
          {SlIcon && (
            <Chip
              icon={<SlIcon sx={{ fontSize: 12, color: slColor }} />}
              label={groupLabel}
              size="small"
              sx={{
                height: 20,
                fontSize: 10,
                bgcolor: `${slColor}18`,
                color: slColor,
                '& .MuiChip-label': { px: 0.5 },
                '& .MuiChip-icon': { ml: 0.25 },
              }}
            />
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
            {anyRecommended && (
              <Tooltip title={selectedAiReason || 'AI Recommended'} arrow>
                <AutoAwesomeIcon sx={{ fontSize: 14, color: aiAccent }} />
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Title + variant count */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            lineHeight: 1.3,
            mb: 0.25,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {group.baseName}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
          {group.items.length} variants
        </Typography>
      </Box>

      {/* Variant selector pills */}
      <Box sx={{ px: 1.5, pb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {group.items.map((variant, idx) => {
          const label = getVariantLabel(variant.title, group.baseName)
          const isSelected = idx === selectedIndex
          const isThisAdded = activePhaseItemIds.has(variant.products_uuid)

          return (
            <Chip
              key={variant.products_uuid}
              label={`${label} · ${variant.points}pts`}
              size="small"
              onClick={() => setSelectedIndex(idx)}
              sx={{
                height: 24,
                fontSize: 11,
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.15s',
                border: '1px solid',
                ...(isThisAdded
                  ? {
                      bgcolor: alpha(theme.palette.text.complete, 0.1),
                      borderColor: alpha(theme.palette.text.complete, 0.5),
                      color: theme.palette.text.complete,
                    }
                  : isSelected
                  ? {
                      bgcolor: alpha(theme.palette.text.ai_accent_secondary, 0.1),
                      borderColor: alpha(theme.palette.text.ai_accent_secondary, 0.4),
                      color: theme.palette.text.ai_accent_secondary,
                    }
                  : {
                      bgcolor: 'transparent',
                      borderColor: 'divider',
                      color: 'text.secondary',
                      '&:hover': { borderColor: 'text.primary', color: 'text.primary' },
                    }),
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          )
        })}
      </Box>

      {/* Points + price for selected variant */}
      <Box sx={{ px: 1.5, pb: 1, display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: slColor }}>
          {selectedItem.points || 0} pts
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          ${dollarValue.toLocaleString()}
        </Typography>
      </Box>

      {/* Action buttons */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderTop: '1px solid',
          borderColor: 'divider',
          mt: 'auto',
        }}
      >
        <Box sx={{ display: 'flex' }}>
          <Tooltip
            title={
              isSelectedAdded
                ? `Already in ${getPhaseShortLabel(activePhase)}`
                : isAnyAdded
                ? `Add variant (${getVariantLabel(addedVariant!.title, group.baseName)} already added)`
                : `Add to ${getPhaseShortLabel(activePhase)}`
            }
          >
            <Box
              component="button"
              onClick={() => !isSelectedAdded && addItem(selectedItem)}
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                py: 0.75,
                border: 'none',
                bgcolor: 'transparent',
                color: isSelectedAdded ? theme.palette.text.complete : 'text.secondary',
                cursor: isSelectedAdded ? 'default' : 'pointer',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'inherit',
                transition: 'all 0.15s',
                '&:hover': !isSelectedAdded
                  ? { bgcolor: alpha(theme.palette.text.complete, 0.08), color: theme.palette.text.complete }
                  : {},
              }}
            >
              {isSelectedAdded ? (
                <CheckCircleIcon sx={{ fontSize: 15 }} />
              ) : (
                <AddCircleOutlineIcon sx={{ fontSize: 15 }} />
              )}
              {isSelectedAdded ? 'Added' : `+ Add to ${getPhaseShortLabel(activePhase)}`}
            </Box>
          </Tooltip>

          {!isSetupPhase && (
            <Tooltip title="Add to multiple months">
              <Box
                component="button"
                onClick={e => handleOpenMultiMonth(e, selectedItem)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 1,
                  py: 0.75,
                  border: 'none',
                  borderLeft: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'transparent',
                  color: 'text.secondary',
                  cursor: 'pointer',
                  fontSize: 11,
                  fontFamily: 'inherit',
                  transition: 'all 0.15s',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.text.ai_accent_secondary, 0.08),
                    color: theme.palette.text.ai_accent_secondary,
                  },
                }}
              >
                <CalendarMonthIcon sx={{ fontSize: 15 }} />
              </Box>
            </Tooltip>
          )}
        </Box>

        {/* Remove row */}
        {allVariantPhases.size > 0 && (
          <Box
            component="button"
            onClick={() => {
              group.items.forEach(item => {
                if (getItemPhases(item.products_uuid).size > 0) removeFromAll(item.products_uuid)
              })
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              py: 0.5,
              border: 'none',
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'transparent',
              color: 'text.disabled',
              cursor: 'pointer',
              fontSize: 11,
              fontFamily: 'inherit',
              transition: 'all 0.15s',
              '&:hover': { bgcolor: alpha(theme.palette.text.error, 0.06), color: theme.palette.text.error },
            }}
          >
            <RemoveCircleOutlineIcon sx={{ fontSize: 13 }} />
            Remove from all ({allVariantPhases.size})
          </Box>
        )}
      </Box>
    </Box>
  )
}
